import { useRef } from "react";
import { SplitText, gsap, useGSAP } from "../../animations/gsapSetup.ts";
import { useReducedMotion } from "../../hooks/useReducedMotion.ts";
import { useI18n } from "../../i18n/context.tsx";
import styles from "./ClassicHero.module.css";

// Classic gradient value (matches --color-classic-gradient in variables.css)
const CLASSIC_GRADIENT = "linear-gradient(135deg, #ff6b00, #ff9500)";

export function ClassicHero() {
  const reducedMotion = useReducedMotion();
  const { t } = useI18n();
  const sectionRef = useRef<HTMLElement>(null);

  useGSAP(
    () => {
      const el = sectionRef.current;
      if (!el) return;

      const wordmark = el.querySelector(`.${styles.wordmark}`) as HTMLElement;
      const subtitle = el.querySelector(`.${styles.subtitle}`) as HTMLElement;

      if (!wordmark || !subtitle) return;

      if (reducedMotion) {
        gsap.set(wordmark, { autoAlpha: 1 });
        gsap.set(subtitle, { autoAlpha: 1 });
        return;
      }

      const bgLayer1 = el.querySelector(`.${styles.bgLayer1}`);
      const bgLayer2 = el.querySelector(`.${styles.bgLayer2}`);

      // SplitText
      const wordmarkSplit = SplitText.create(wordmark, { type: "chars" });
      const subtitleSplit = SplitText.create(subtitle, { type: "words" });

      // Fix: gradient-clip-text breaks with SplitText child divs
      wordmark.style.background = "none";
      wordmark.style.webkitTextFillColor = "";
      wordmark.style.color = "#fff";
      for (const c of wordmarkSplit.chars) {
        if (c instanceof HTMLElement) {
          c.style.backgroundImage = CLASSIC_GRADIENT;
          c.style.backgroundClip = "text";
          c.style.webkitBackgroundClip = "text";
          c.style.webkitTextFillColor = "transparent";
          c.style.display = "inline-block";
        }
      }

      const tl = gsap.timeline({ defaults: { ease: "power4.out" } });

      tl.set(wordmark, { autoAlpha: 1 })
        .from(wordmarkSplit.chars, {
          y: "110%",
          rotateX: -45,
          opacity: 0,
          filter: "blur(6px)",
          duration: 0.9,
          stagger: 0.04,
          onComplete: () => {
            for (const c of wordmarkSplit.chars) {
              if (c instanceof HTMLElement) c.style.filter = "";
            }
          },
        })
        .set(subtitle, { autoAlpha: 1 }, "-=0.3")
        .from(subtitleSplit.words, { y: 30, opacity: 0, duration: 0.7, stagger: 0.05 }, "-=0.3");

      // Parallax background layers
      if (bgLayer1 && bgLayer2) {
        gsap.to(bgLayer1, {
          yPercent: -30,
          scrollTrigger: { trigger: el, start: "top top", end: "bottom top", scrub: 1 },
        });
        gsap.to(bgLayer2, {
          yPercent: -15,
          scale: 1.1,
          scrollTrigger: { trigger: el, start: "top top", end: "bottom top", scrub: 1 },
        });
      }
    },
    {
      scope: sectionRef,
      // 言語切替時の再アニメーション:
      // useGSAPはdependencies変更時にコンテキストを自動revert(SplitTextのDOM復元含む)
      // → 新テキストで再split → 再アニメーション。
      // これがないと旧言語のSplitText DOMが残り、テキスト描画が壊れる。
      dependencies: [t.classic.heroWordmark, t.classic.heroSubtitle, reducedMotion],
    },
  );

  return (
    <section ref={sectionRef} className={styles.hero}>
      <div className={styles.bg}>
        <div className={styles.bgLayer1} />
        <div className={styles.bgLayer2} />
      </div>
      <div className={styles.content}>
        <h1 className={styles.wordmark} style={{ visibility: "hidden" }}>
          {t.classic.heroWordmark.split("\n").map((line, i, arr) => (
            <span key={line}>
              {line}
              {i < arr.length - 1 && <br />}
            </span>
          ))}
        </h1>
        <p className={styles.subtitle} style={{ visibility: "hidden" }}>
          {t.classic.heroSubtitle}
        </p>
      </div>
    </section>
  );
}
